import {useCallback, useEffect, useRef, useState} from 'react';
import {ConfigService} from '../../../../utils';

export const useSignaling = (tourId: string) => {
  const [isSyncActive, setIsSyncActive] = useState(false);
  const [
    signalingConnection,
    setSignalingConnection,
  ] = useState<WebSocket | null>(null);
  const [signalingError, setSignalingError] = useState<Error | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const connectToSignaling = useCallback(async () => {
    const MAX_RECONNECT_ATTEMPTS = 3;
    const RECONNECT_DELAY = 3000; // 3 seconds

    try {
      const ws = new WebSocket(
        `${ConfigService.serverUri}/ws/tour-viewer-events?tourId=${tourId}`
      );

      ws.onopen = () => {
        setSignalingConnection(ws);
        setIsSyncActive(true);
        setSignalingError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'truvu_scrolling') {
            window.postMessage(
              {
                type: 'truvu_marker',
                frameIndex: data.data.frameIndex,
              },
              '*'
            );
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        // eslint-disable-next-line no-console
        console.error('WebSocket error:', error);
        setSignalingError(new Error('WebSocket connection error'));
        ws.close();
      };

      ws.onclose = () => {
        setSignalingConnection(null);
        setIsSyncActive(false);

        // Attempt reconnect if it wasn't a deliberate close
        if (
          isSyncActive &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          reconnectAttemptsRef.current += 1;
          reconnectTimerRef.current = setTimeout(
            connectToSignaling,
            RECONNECT_DELAY
          );
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setSignalingError(
            new Error('Failed to reconnect after multiple attempts')
          );
        }
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating WebSocket:', error);
      setSignalingError(new Error('Failed to create WebSocket connection'));
    }
  }, [tourId, isSyncActive]);

  const toggleSync = useCallback(async () => {
    if (isSyncActive) {
      if (signalingConnection) {
        signalingConnection.close();
      }
      setSignalingConnection(null);
      setIsSyncActive(false);
      setSignalingError(null);

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
    } else {
      await connectToSignaling();
    }
  }, [isSyncActive, signalingConnection, connectToSignaling]);

  useEffect(() => {
    return () => {
      if (signalingConnection) {
        signalingConnection.close();
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [signalingConnection]);

  return {
    isSyncActive,
    signalingConnection,
    signalingError,
    toggleSync,
    resetSignaling: () => {
      if (signalingConnection) {
        signalingConnection.close();
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      setSignalingConnection(null);
      setIsSyncActive(false);
      setSignalingError(null);
      reconnectAttemptsRef.current = 0;
    },
  };
};
